import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { keyframes }   from '@emotion/react';
import SectionHeader   from './SectionHeader';
import { C, F }        from '../styles/tokens';

// ── Data ───────────────────────────────────────────────────────────────────────

const ALL_TESTIMONIALS = [
  { name: 'Amit Sharma',   role: 'Startup Founder, Delhi',    text: 'Got my company registered in just 3 days. Pracco made the entire process super smooth!',                                    initials: 'AS', color: C.blue },
  { name: 'Priya Mehta',   role: 'Freelance Consultant',      text: 'Finally got my GST done without the usual headache. Highly recommend to every small business owner.',                        initials: 'PM', color: C.amber },
  { name: 'Rohit Verma',   role: 'SME Owner, Noida',          text: 'Team was super helpful on WhatsApp. Resolved every query within minutes. Fantastic service!',                               initials: 'RV', color: '#7C3AED' },
  { name: 'Neha Agarwal',  role: 'E-commerce Seller',         text: 'Very professional. They explained every document clearly before I signed anything. Felt safe throughout.',                  initials: 'NA', color: C.green },
  { name: 'Vikram Singh',  role: 'Retail Business Owner',     text: 'Quick, reliable and completely honest about charges. No hidden fees. Will use again for sure!',                             initials: 'VS', color: '#0891B2' },
  { name: 'Shubham Kumar', role: 'CEO, KnowledgeEdgeAI',      text: "Super responsive team. We've seen real benefits partnering with Pracco for our compliance needs.",                          initials: 'SK', color: C.blue,   href: 'https://knowledgeedgeai.com/' },
  { name: 'Sunita Joshi',  role: 'Restaurant Owner, Gurgaon', text: 'FSSAI and GST registration both completed seamlessly. Saved me so much time and stress!',                                  initials: 'SJ', color: C.amber },
  { name: 'Deepak Nair',   role: 'IT Professional',           text: 'ITR filing was a breeze. No stress, no confusion — they guided me at every step.',                                         initials: 'DN', color: '#7C3AED' },
  { name: 'Ritika Sinha',  role: 'Client',                    text: 'Communication was excellent throughout. The team is genuinely warm and knowledgeable.',                                     initials: 'RS', color: C.green },
  { name: 'Rahul Kumar',   role: 'Client',                    text: 'Great experience overall! Impressed with the quality, speed, and attention to detail.',                                     initials: 'RK', color: '#0891B2' },
];

// Distribute testimonials across 3 columns
const COLUMNS = [
  [0, 3, 6, 9].map((i) => ALL_TESTIMONIALS[i]),
  [1, 4, 7].map((i) => ALL_TESTIMONIALS[i]),
  [2, 5, 8].map((i) => ALL_TESTIMONIALS[i]),
];

// Each column scrolls at a slightly different speed for organic feel
const DURATIONS = [26, 22, 30]; // seconds

// ── CSS scroll animation ───────────────────────────────────────────────────────

const scrollUp = keyframes`
  0%   { transform: translateY(0); }
  100% { transform: translateY(-50%); }
`;

// ── Count-up hook ──────────────────────────────────────────────────────────────

const useCountUp = (target, duration = 1800) => {
  const [count,   setCount]   = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.4 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [started, target, duration]);

  return { count, ref };
};

const formatCount = (val) => {
  if (val >= 10000) return '10k+';
  if (val >= 1000) {
    const k = val / 1000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k+`;
  }
  if (val >= 100) return `${Math.floor(val / 100) * 100}+`;
  return `${val}+`;
};

// ── Single testimonial card ────────────────────────────────────────────────────

const Card = ({ t }) => (
  <Box
    sx={{
      backgroundColor: C.white,
      border:          `1px solid ${C.border}`,
      borderLeft:      `3px solid ${t.color}`,
      borderRadius:    '14px',
      p:               2.5,
      boxShadow:       '0 4px 16px rgba(15,32,64,0.07)',
      flexShrink:      0,
    }}
  >
    <FormatQuoteIcon sx={{ color: t.color, fontSize: 18, mb: 0.75, opacity: 0.75 }} />
    <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, lineHeight: 1.65, mb: 1.75, fontStyle: 'italic' }}>
      "{t.text}"
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar sx={{ width: 28, height: 28, bgcolor: t.color, fontFamily: F.heading, fontWeight: 700, fontSize: 11 }}>
        {t.initials}
      </Avatar>
      <Box>
        <Typography
          component={t.href ? 'a' : 'span'}
          href={t.href}
          target={t.href ? '_blank' : undefined}
          rel={t.href ? 'noopener noreferrer' : undefined}
          sx={{
            fontFamily:     F.heading,
            fontWeight:     700,
            fontSize:       12,
            color:          C.navy,
            display:        'block',
            lineHeight:     1.2,
            textDecoration: 'none',
            '&:hover':      t.href ? { color: C.blue } : {},
          }}
        >
          {t.name}
        </Typography>
        <Typography sx={{ fontFamily: F.body, fontSize: 10.5, color: C.textMuted }}>
          {t.role}
        </Typography>
      </Box>
    </Box>
  </Box>
);

// ── Scrolling column ───────────────────────────────────────────────────────────

const Column = ({ items, duration }) => {
  const doubled = [...items, ...items]; // duplicate for seamless loop
  return (
    <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
      <Box
        sx={{
          display:        'flex',
          flexDirection:  'column',
          gap:            2,
          animation:      `${scrollUp} ${duration}s linear infinite`,
          '&:hover':      { animationPlayState: 'paused' },
        }}
      >
        {doubled.map((t, i) => <Card key={i} t={t} />)}
      </Box>
    </Box>
  );
};

// ── Animated stat value ────────────────────────────────────────────────────────

const AnimatedValue = ({ target, display, label }) => {
  const { count, ref } = useCountUp(target, 1800);
  return (
    <Box ref={ref} sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontFamily: F.heading, fontWeight: 800, fontSize: { xs: 26, md: 32 }, color: C.navy, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {target ? formatCount(count) : display}
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, mt: 0.25 }}>
        {label}
      </Typography>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const Testimonials = () => (
  <Box id="testimonials" sx={{ backgroundColor: C.bg, px: { xs: 2, md: 6 }, py: { xs: 7, md: 10 } }}>
    <SectionHeader
      label="Client Stories"
      title="What Our Clients Say"
      subtitle="Real feedback from real people who trusted us with their compliance."
    />

    {/* Scrolling columns stage */}
    <Box
      sx={{
        position:     'relative',
        height:       { xs: 380, md: 460 },
        maxWidth:     1000,
        mx:           'auto',
        overflow:     'hidden',
        borderRadius: '20px',
        border:       `1px solid ${C.border}`,
        backgroundColor: C.blueLight,
        mb:           4,
      }}
    >
      {/* Dot grid background */}
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(${C.border} 1px, transparent 1px)`, backgroundSize: '28px 28px', opacity: 0.55, pointerEvents: 'none' }} />

      {/* Top + bottom fade */}
      <Box sx={{ position: 'absolute', top: 0,    left: 0, right: 0, height: 64, background: `linear-gradient(to bottom, ${C.blueLight}, transparent)`, zIndex: 2, pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 64, background: `linear-gradient(to top,    ${C.blueLight}, transparent)`, zIndex: 2, pointerEvents: 'none' }} />


      {/* Columns */}
      <Box
        sx={{
          display:               'grid',
          gridTemplateColumns:   { xs: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap:                   2,
          height:                '100%',
          px:                    2,
          pt:                    2,
          pb:                    2,
          boxSizing:             'border-box',
        }}
      >
        {COLUMNS.map((items, i) => (
          <Box
            key={i}
            sx={{
              display:   i === 2 ? { xs: 'none', md: 'block' } : 'block',
              overflow:  'hidden',
              height:    '100%',
            }}
          >
            <Column items={items} duration={DURATIONS[i]} />
          </Box>
        ))}
      </Box>
    </Box>

    {/* Summary stats */}
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 3, md: 5 }, flexWrap: 'wrap' }}>
      <AnimatedValue target={10000} label="Happy Clients" />
      <AnimatedValue target={0} display="4.9" label="Average Rating" />
      <AnimatedValue target={0} display="98%" label="Recommend Us" />
    </Box>
  </Box>
);

export default Testimonials;
